// Lightweight, mobile-first welcome page shown at /join/CODE. Fires the
// referral attribution (via App.js boot-time `captureReferralFromUrl`) and
// presents three focused CTAs:
//   • Open SpotDrop        → /app
//   • Get SpotDrop         → reuses the same <InstallButton> as landing
//   • Continue without…    → /app (no-install opt-out)
//
// Zero prize/campaign wording — always neutral community copy so the page
// remains safe while the fuel-voucher campaign is inactive.

import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Users, Sparkles, Trophy, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { InstallButton } from "../components/InstallCTA";
import { CampaignJoinModal } from "../components/CampaignCard";
import { getReferredBy } from "../lib/referral";
import { fetchPublicCampaign } from "../lib/campaign";
import { api } from "../lib/api";

const CODE_RE = /^[A-Z0-9]{6}$/;

export default function JoinWelcome() {
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const code = (rawCode || "").toUpperCase();
  const codeValid = CODE_RE.test(code);

  // Hooks must run unconditionally — attribution state polling.
  const [attributed, setAttributed] = React.useState(() =>
    codeValid ? getReferredBy() === code : false
  );
  React.useEffect(() => {
    if (!codeValid || attributed) return undefined;
    // captureReferralFromUrl fires visit → attribute; a fresh device usually
    // completes in <400 ms. Poll for up to ~4 s in case of slow networks.
    let cancelled = false;
    const start = Date.now();
    const tick = () => {
      if (cancelled) return;
      if (getReferredBy() === code) {
        setAttributed(true);
        return;
      }
      if (Date.now() - start > 4000) return;
      setTimeout(tick, 200);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [attributed, code, codeValid]);

  const goToApp = React.useCallback(() => {
    navigate("/app", { replace: false });
  }, [navigate]);

  // Campaign state controls whether we show the giveaway-focused hero.
  const [campaign, setCampaign] = React.useState(null);
  const [joinOpen, setJoinOpen] = React.useState(false);
  React.useEffect(() => {
    fetchPublicCampaign().then(setCampaign);
  }, []);

  // Belt-and-braces visit tracking: `captureReferralFromUrl` in App.js
  // already fires /referrals/visit on boot, but if a subtle race in the
  // route match ever skips it, we retry once here as a safety net. The
  // backend dedups by (code, device_id) so this stays idempotent.
  React.useEffect(() => {
    if (!codeValid) return;
    // Only retry after the boot handler has had time to run.
    const t = setTimeout(() => {
      try {
        const key = "spotdrop_ref_visit_" + code;
        if (sessionStorage.getItem(key) === "1") return;
        sessionStorage.setItem(key, "1");
        api.post("/referrals/visit", { code }).catch(() => {});
      } catch {
        /* ignore */
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [code, codeValid]);

  // Sanity guard — malformed code goes straight to the landing.
  if (!codeValid) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className="min-h-screen bg-[#0a0a0c] text-white flex flex-col"
      data-testid="join-welcome"
    >
      {/* Compact top bar — brand only, no full landing nav. */}
      <header className="px-5 py-5 md:px-10 md:py-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="join-brand">
          <div className="w-8 h-8 rounded-full bg-[#ccff00] flex items-center justify-center">
            <MapPin className="w-4 h-4 text-[#0a0a0c]" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">SpotDrop.</span>
        </a>
        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
          Malta
        </span>
      </header>

      <main className="flex-1 flex items-center px-5 md:px-10">
        <div className="w-full max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#ccff00]/40 bg-[#ccff00]/[0.06] text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold"
              data-testid="join-invited-badge"
            >
              {campaign?.active ? <Trophy className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              {campaign?.active ? "You were invited to the giveaway" : "You were invited"}
            </div>

            <h1
              className="mt-5 font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight"
              data-testid="join-title"
            >
              {campaign?.active
                ? "Win €50 of fuel with SpotDrop."
                : "A friend invited you to SpotDrop."}
            </h1>

            <p className="mt-4 text-base text-zinc-400 leading-relaxed" data-testid="join-copy">
              {campaign?.active ? (
                <>
                  We&apos;re giving away <strong className="text-white">2 × €50 fuel vouchers</strong> in
                  Malta. Free to enter · no account required.
                </>
              ) : (
                <>
                  SpotDrop helps drivers in Malta find and share parking spots in real time.
                  Free to use — no sign-up required.
                </>
              )}
            </p>

            <div
              className="mt-5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 flex items-center gap-2 text-xs text-zinc-400"
              data-testid="join-code-chip"
            >
              <Users className="w-3.5 h-3.5 text-[#ccff00]" />
              <span>Invite code:</span>
              <span className="font-mono text-white">{code}</span>
              <span
                className={`ml-auto text-[10px] uppercase tracking-[0.18em] font-semibold ${
                  attributed ? "text-[#ccff00]" : "text-zinc-500"
                }`}
                data-testid="join-attribution-state"
              >
                {attributed ? "Saved" : "Saving…"}
              </span>
            </div>

            {/* CTA stack — giveaway-first when campaign active. */}
            <div className="mt-8 flex flex-col gap-3">
              {campaign?.active ? (
                <>
                  <Button
                    type="button"
                    onClick={() => setJoinOpen(true)}
                    className="w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold text-base"
                    data-testid="join-enter-giveaway"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Enter the Giveaway
                  </Button>
                  <Button
                    type="button"
                    onClick={goToApp}
                    variant="outline"
                    className="w-full h-11 rounded-full bg-transparent border border-white/15 text-white hover:bg-white/5 font-medium text-sm"
                    data-testid="join-open-app"
                  >
                    Open SpotDrop
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    onClick={goToApp}
                    className="w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold text-base"
                    data-testid="join-open-app"
                  >
                    Open SpotDrop
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </>
              )}

              <InstallButton
                variant="outline"
                label="Get SpotDrop"
                className="w-full"
                testId="join-get-spotdrop"
              />

              <button
                type="button"
                onClick={goToApp}
                className="w-full h-11 text-sm text-zinc-400 hover:text-white transition-colors underline-offset-4 hover:underline"
                data-testid="join-continue-without-install"
              >
                Continue without installing
              </button>
            </div>

            <p className="mt-6 text-[11px] text-zinc-600 leading-relaxed">
              We save this invite once, on this device. Opening SpotDrop before installing
              won&apos;t lose the invite.
            </p>
          </motion.div>
        </div>
      </main>

      <footer className="px-5 md:px-10 py-5 text-center">
        <a href="/" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors" data-testid="join-back-landing">
          Learn more about SpotDrop
        </a>
      </footer>

      <CampaignJoinModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onJoined={() => {
          setJoinOpen(false);
          navigate("/app", { replace: false });
        }}
      />
    </div>
  );
}
